import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  CategoriesService,
  CategoryDto,
} from '../../../services/categories/categories.service';
import { ExperienceService } from '../../../services/experience/experience.service';
import { ImageVariantBackfillService } from '../../../services/utils/image-variant-backfill.service';
import { CMSSection, SectionType, Tab } from './types';

import { AppTheme, ThemeService } from '../../../services/theme/theme.service';
import { MockPreviewComponent } from './components/mock-preview/mock-preview.component';
import { SectionConfigComponent } from './components/section-config/section-config.component';
import { TabModalComponent } from './components/tab-modal/tab-modal.component';

export interface ToastMessage {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
}

export const SECTION_TYPES: {
  type: SectionType;
  label: string;
  icon: string;
  description: string;
}[] = [
  {
    type: 'banner',
    label: 'Banner Carousel',
    icon: 'fi-rs-picture',
    description: 'Scrollable hero banners',
  },
  {
    type: 'category_grid',
    label: 'Category Grid',
    icon: 'fi-rs-apps',
    description: 'Category tiles grid',
  },
  {
    type: 'brand_grid',
    label: 'Brand Grid',
    icon: 'fi-rs-star',
    description: 'Featured brands showcase',
  },
  {
    type: 'collection_banner',
    label: 'Collection Banner',
    icon: 'fi-rs-picture',
    description: 'Campaign header with category cards',
  },
  {
    type: 'featured_cards',
    label: 'Featured Cards',
    icon: 'fi-rs-gallery',
    description: 'Horizontal editorial cards used by the customer app',
  },
  {
    type: 'promo_carousel',
    label: 'Promo Carousel',
    icon: 'fi-rs-ad',
    description: 'Wide promotional shop-now tiles',
  },
  {
    type: 'product_shelf',
    label: 'Product Shelf',
    icon: 'fi-rs-box-alt',
    description: 'Horizontal product row',
  },
];

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MockPreviewComponent,
    SectionConfigComponent,
    TabModalComponent,
  ],
  providers: [],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit {
  private experienceService = inject(ExperienceService);
  private categoriesService = inject(CategoriesService);
  private themeService = inject(ThemeService);
  private imageVariantBackfill = inject(ImageVariantBackfillService);
  private sanitizer = inject(DomSanitizer);

  // ─── State ───────────────────────────────────────────────────────────────
  isLoading = false;
  isBackfillingImages = false;
  isSaving = false;
  isDirty = false; // tracks unsaved local changes
  tabs: Tab[] = [];
  activeTabId = '';
  selectedSectionId = '';
  showAddSection = false;
  availableCategories: CategoryDto[] = [];

  // ─── Delete confirmation (inline) ─────────────────────────────────────────
  pendingDeleteTabId: string | null = null;
  pendingDeleteSectionId: string | null = null;

  // ─── Tab Modal ────────────────────────────────────────────────────────────
  isTabModalOpen = false;
  isEditingTab = false;
  editingTabId = '';
  themes: AppTheme[] = [];
  sectionTypes = SECTION_TYPES;

  newTabForm: Partial<Tab> = {
    name: '',
    theme_name: '',
    search_placeholder: 'Search...',
    is_active: false,
  };

  // ─── Toasts ───────────────────────────────────────────────────────────────
  toasts: ToastMessage[] = [];
  private toastCounter = 0;
  studioStats = [
    { label: 'Tabs', value: () => this.tabs.length },
    {
      label: 'Sections',
      value: () =>
        this.tabs.reduce((count, tab) => count + tab.sections.length, 0),
    },
    {
      label: 'Visible',
      value: () =>
        this.tabs.reduce(
          (count, tab) =>
            count + tab.sections.filter((section) => section.is_active).length,
          0
        ),
    },
  ];

  get activeTab(): Tab | undefined {
    return this.tabs.find((t) => t.id === this.activeTabId);
  }

  get selectedSection(): CMSSection | undefined {
    return this.activeTab?.sections.find(
      (s) => s.id === this.selectedSectionId
    );
  }

  ngOnInit() {
    this.loadLayout();
    this.loadThemes();
    this.categoriesService.getCategoryTreeAll().subscribe((cats) => {
      this.availableCategories = this.filterActiveCategories(cats);
    });
  }

  private filterActiveCategories(cats: CategoryDto[]): CategoryDto[] {
    return cats
      .filter((c) => c.isActive)
      .map((c) => ({
        ...c,
        children: c.children ? this.filterActiveCategories(c.children) : [],
      }));
  }

  loadThemes() {
    this.themeService.getThemes().subscribe((data) => {
      this.themes = data.filter((theme) => theme.status === 'published');

      if (
        !this.newTabForm.id &&
        !this.newTabForm.theme_id &&
        this.themes.length > 0
      ) {
        const activeTheme = this.themes[0];

        this.newTabForm.theme_id = activeTheme.id;
        this.newTabForm.theme_name = activeTheme.name;
      }
    });
  }

  loadLayout() {
    this.isLoading = true;
    this.isDirty = false;
    this.experienceService.getTabs().subscribe({
      next: (data) => {
        this.tabs = data;
        if (this.tabs.length > 0 && !this.activeTabId) {
          this.activeTabId = this.tabs[0].id;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load tabs', err);
        this.toast('error', 'Failed to load layout. Please refresh.');
        this.isLoading = false;
      },
    });
  }

  // ─── Dirty Tracking ───────────────────────────────────────────────────────
  markDirty() {
    this.isDirty = true;
  }

  // ─── Tab Management ───────────────────────────────────────────────────────
  selectTab(id: string) {
    this.activeTabId = id;
    this.selectedSectionId = '';
    this.showAddSection = false;
    this.pendingDeleteTabId = null;
  }

  openTabModal(tab?: Tab) {
    if (tab) {
      this.isEditingTab = true;
      this.editingTabId = tab.id;
      this.newTabForm = { ...tab };
    } else {
      this.isEditingTab = false;
      this.editingTabId = '';
      const defaultTheme = this.themes[0];
      this.newTabForm = {
        name: '',
        theme_id: defaultTheme?.id,
        theme_name: defaultTheme?.name || '',
        search_placeholder: 'Search...',
        is_active: false,
      };
    }
    this.isTabModalOpen = true;
  }

  closeTabModal() {
    this.isTabModalOpen = false;
  }

  saveTab() {
    if (!this.newTabForm.name?.trim()) return;

    const selectedTheme =
      this.themes.find((theme) => theme.id === this.newTabForm.theme_id) ||
      this.themes.find((theme) => theme.name === this.newTabForm.theme_name) ||
      this.themes[0];

    this.newTabForm.theme_id = selectedTheme?.id;
    this.newTabForm.theme_name = selectedTheme?.name || '';

    if (this.isEditingTab) {
      const idx = this.tabs.findIndex((t) => t.id === this.editingTabId);
      if (idx !== -1) {
        this.tabs[idx] = { ...this.tabs[idx], ...this.newTabForm } as Tab;
      }
    } else {
      const rawName = this.newTabForm.name ?? '';
      const slug = rawName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const newTab: Tab = {
        id: 'tab_' + Math.random().toString(36).substring(2, 11),
        name: rawName,
        slug,
        icon_url: this.newTabForm.icon_url || '',
        icon_file_id: this.newTabForm.icon_file_id || null,
        icon_bucket: this.newTabForm.icon_bucket || '',
        icon_path: this.newTabForm.icon_path || '',
        _pending_file: this.newTabForm._pending_file,
        theme_id: this.newTabForm.theme_id,
        theme_name: this.newTabForm.theme_name || '',
        search_placeholder: this.newTabForm.search_placeholder || 'Search...',
        position: this.tabs.length,
        is_active: this.newTabForm.is_active ?? false,
        sections: [],
      };
      this.tabs.push(newTab);
      this.activeTabId = newTab.id;
    }

    this.closeTabModal();
    this.markDirty();
    this.toast('info', 'Tab staged. Publish to save it to the app.');
  }

  toggleTabActive(tab: Tab, event: Event) {
    event.stopPropagation();
    tab.is_active = !tab.is_active;
    this.markDirty();
  }

  // ─── Inline Delete Confirmation ───────────────────────────────────────────
  requestDeleteTab(id: string, event?: Event) {
    event?.stopPropagation();
    // toggle: clicking same tab again cancels
    this.pendingDeleteTabId = this.pendingDeleteTabId === id ? null : id;
    this.pendingDeleteSectionId = null;
  }

  confirmDeleteTab(id: string, event?: Event) {
    event?.stopPropagation();
    const tab = this.tabs.find((t) => t.id === id);
    if (!tab) return;

    const isNew = tab.id.startsWith('tab_') || isNaN(Number(tab.id));
    if (!isNew) {
      this.experienceService.deleteTab(id).subscribe({
        next: () => {
          this.removeTab(id);
          this.toast('success', `Tab "${tab.name}" deleted.`);
        },
        error: () => this.toast('error', 'Failed to delete tab from database.'),
      });
      return;
    }

    this.removeTab(id);
  }

  private removeTab(id: string) {
    this.tabs = this.tabs.filter((t) => t.id !== id);
    if (this.activeTabId === id) {
      this.activeTabId = this.tabs[0]?.id || '';
    }
    this.pendingDeleteTabId = null;
    this.markDirty();
  }

  cancelDeleteTab(event?: Event) {
    event?.stopPropagation();
    this.pendingDeleteTabId = null;
  }

  onTabsReordered(event: CdkDragDrop<Tab[]>) {
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    this.markDirty();
  }

  // ─── Section Management ───────────────────────────────────────────────────
  selectSection(id: string) {
    if (id === 'tab_nav') return;
    this.selectedSectionId = id;
    this.showAddSection = false;
    this.pendingDeleteSectionId = null;
  }

  toggleAddSection() {
    this.showAddSection = !this.showAddSection;
    if (this.showAddSection) this.selectedSectionId = '';
  }

  addSection(type: SectionType) {
    if (!this.activeTab) return;
    const newSection: CMSSection = {
      id: 'sec_' + Math.random().toString(36).substring(2, 11),
      tab_id: this.activeTabId,
      type,
      name: this.getLabelForType(type),
      position: this.activeTab.sections.length,
      is_active: true,
      config: this.getDefaultConfigForType(type),
    };
    this.activeTab.sections.push(newSection);
    this.selectedSectionId = newSection.id;
    this.showAddSection = false;
    this.markDirty();
  }

  toggleSectionActive(section: CMSSection, event: Event) {
    event.stopPropagation();
    section.is_active = !section.is_active;
    this.markDirty();
  }

  requestDeleteSection(id: string, event?: Event) {
    event?.stopPropagation();
    this.pendingDeleteSectionId =
      this.pendingDeleteSectionId === id ? null : id;
  }

  confirmDeleteSection(id: string, event?: Event) {
    event?.stopPropagation();
    if (!this.activeTab) return;

    const section = this.activeTab.sections.find((s) => s.id === id);
    if (!section) return;

    const isNew = section.id.startsWith('sec_') || isNaN(Number(section.id));
    if (!isNew) {
      this.experienceService.deleteSection(id).subscribe({
        next: () => this.toast('success', 'Section deleted.'),
        error: () =>
          this.toast('error', 'Failed to delete section from database.'),
      });
    }

    this.activeTab.sections = this.activeTab.sections.filter(
      (s) => s.id !== id
    );
    this.pendingDeleteSectionId = null;
    if (this.selectedSectionId === id) this.selectedSectionId = '';
    this.markDirty();
  }

  cancelDeleteSection(event?: Event) {
    event?.stopPropagation();
    this.pendingDeleteSectionId = null;
  }

  onSectionsReordered(event: CdkDragDrop<any[]>) {
    if (!this.activeTab) return;
    moveItemInArray(
      this.activeTab.sections,
      event.previousIndex,
      event.currentIndex
    );
    this.markDirty();
  }

  // ─── Publish ──────────────────────────────────────────────────────────────
  saveLayout() {
    if (this.isSaving) return;

    // Older tabs may only have the theme name in meta. Resolve it before every
    // publish so the customer-side join can always load the selected theme.
    this.tabs = this.tabs.map((tab) => {
      const selectedTheme =
        this.themes.find((theme) => theme.id === tab.theme_id) ||
        this.themes.find((theme) => theme.name === tab.theme_name);

      return selectedTheme
        ? {
            ...tab,
            theme_id: selectedTheme.id,
            theme_name: selectedTheme.name,
          }
        : tab;
    });

    this.isSaving = true;
    this.experienceService.saveLayout(this.tabs).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDirty = false;
        this.toast('success', 'Layout published to app successfully.');
        this.loadLayout();
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Publish failed:', err);
        this.toast('error', 'Publish failed. Please try again.');
      },
    });
  }

  async backfillActiveImages() {
    if (this.isBackfillingImages) return;
    if (!window.confirm('Generate optimized variants for active Home and catalog images?')) {
      return;
    }
    this.isBackfillingImages = true;
    try {
      const report = await this.imageVariantBackfill.runActiveHomeBackfill();
      this.toast(
        report.failed.length ? 'warning' : 'success',
        `Image migration: ${report.migrated} migrated, ${report.skipped} already ready, ${report.failed.length} failed.`,
      );
      if (report.migrated > 0) this.loadLayout();
    } catch {
      this.toast('error', 'Image migration failed before it could complete.');
    } finally {
      this.isBackfillingImages = false;
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────
  getSectionIcon(type: SectionType): string {
    const map: Record<string, string> = {
      banner: 'fi-rs-picture',
      offer_strip: 'fi-rs-ticket',
      category_grid: 'fi-rs-apps',
      brand_grid: 'fi-rs-star',
      collection_banner: 'fi-rs-picture',
      featured_cards: 'fi-rs-gallery',
      promo_carousel: 'fi-rs-ad',
      flash_sale: 'fi-rs-bolt',
      product_shelf: 'fi-rs-box-alt',
      hero_showcase: 'fi-rs-sparkles',
    };
    return map[type] || 'fi-rs-layers';
  }

  private getLabelForType(type: SectionType): string {
    return SECTION_TYPES.find((s) => s.type === type)?.label || type;
  }

  private getDefaultConfigForType(type: SectionType): any {
    switch (type) {
      case 'banner':
        return {
          data: {
            banners: [
              {
                image_url: '',
                link_url: '',
              },
            ],
            auto_scroll: true,
            aspect_ratio: '21:9',
            scroll_interval_ms: 3000,
          },
          version: 1,
        };
      case 'offer_strip':
        return {
          data: {
            title: 'Extra 10% OFF',
            subtitle: 'Use code SUPIDOO10',
            background_color: '#fffbeb',
            text_color: '#92400e',
            cta_text: 'Copy',
            ui_type: 'classic',
            icon: 'fi-rs-ticket',
          },
          version: 1,
        };
      case 'category_grid':
        return {
          data: {
            title: 'Shop by Category',
            categories: [],
            columns: 4,
            show_labels: true,
          },
          version: 1,
        };
      case 'brand_grid':
        return {
          data: {
            title: 'Shop by Brands',
            brands: [],
          },
          version: 1,
        };
      case 'flash_sale':
        return {
          data: {
            title: 'Flash Sale',
            end_time: '',
            items: [],
          },
          version: 1,
        };
      case 'product_shelf':
        return {
          data: {
            title: 'Best Sellers',
            subtitle: 'Top picks for you',
            ui_type: 'grid',
            indicator_icon: 'fi-rs-star',
            discount_label: 'UP TO 50% OFF',
            data_source: {
              type: 'query',
              query: { limit: 10, sort_by: 'sales' },
              products: [],
            },
            show_see_all: true,
          },
          version: 1,
        };
      case 'collection_banner':
        return {
          data: {
            title: 'Salon',
            subtitle: 'at Home',
            background_type: 'solid',
            background_color: '#7A2E0E',
            background_color_end: '#F6D7A4',
            gradient_angle: 180,
            layout_type: 'hero_mosaic',
            render_main_banner: true,
            edge_to_edge: true,
            show_top_border: true,
            show_bottom_border: true,
            border_style: 'scallop',
            border_color: '#FFF6E4',
            border_under_color: '#FFFFFF',
            text_style: 'teej',
            chip_style: 'circle',
            chip_selected_text_color: '#B45309',
            chip_unselected_text_color: '#5A2A0A',
            chip_selected_border_color: '#B45309',
            product_display_style: 'grid',
            title_color: '#5A2A0A',
            subtitle_color: '#5A2A0A',
            text_color: '#5A2A0A',
            show_bottom_offer: false,
            main_banner: {
              title: 'Salon',
              subtitle: 'at Home',
              brand: 'Vatika',
              cta_text: 'Hair Routine',
              image_url: '',
              link_url: '',
            },
            items: [
              {
                title: 'DEALS',
                subtitle: 'Corner',
                badge: '₹699',
                mrp: '₹795',
                image_url: '',
                background_color: '#fff',
              },
              {
                title: 'Premium Hair Care Range',
                badge: 'STARTING ₹149',
                image_url: '',
                background_color: '#fff',
              },
              {
                title: 'Daily Hair Care',
                badge: 'STARTS AT ₹139',
                image_url: '',
                background_color: '#fff',
              },
              {
                title: 'Hair Colour & Styling',
                badge: 'STARTS AT ₹35',
                image_url: '',
                background_color: '#fff',
              },
              {
                title: 'Hair Growth & Supplement',
                badge: 'UP TO 60% OFF',
                image_url: '',
                background_color: '#fff',
              },
            ],
            bottom_offer: {
              title: 'Buy 2 get Upto 15% Off',
              subtitle:
                'Loreal, Garnier & L’oreal Professional, *T & C applied',
              image_url: '',
              design: 'card',
              images: [{ image_url: '' }, { image_url: '' }, { image_url: '' }],
            },
          },
          version: 1,
        };
      case 'featured_cards':
        return {
          data: {
            title: 'Featured this week',
            card_style: 'rounded_square',
            items: [
              {
                title: 'New Arrivals',
                badge: 'FRESH',
                background_color: '#ffe7a6',
              },
            ],
          },
          version: 1,
        };
      case 'promo_carousel':
        return {
          data: {
            cards: [
              {
                title: 'Exclusive Weekend Offer',
                subtitle: 'Save more on fruits',
                background_color: '#f8ddf7',
              },
            ],
          },
          version: 1,
        };
      case 'hero_showcase':
        return {
          data: {
            layout_type: 'banner_top_cards_bottom',
            ui_type: 'premium',
            container: {
              background_color: '#EEF2FF',
              border_radius: 24,
              padding: 16,
              shadow: true,
            },
            banner: {
              title: 'Summer Electronics',
              subtitle: 'Up to 75% OFF',
              cta_text: 'Shop Now',
              image_url: '',
              link_url: '',
            },
            cards: [
              {
                title: 'Headphones',
                badge: '25% OFF',
                image_url: '',
                background_color: '#FFF',
              },
              {
                title: 'Smart Watches',
                badge: '30% OFF',
                image_url: '',
                background_color: '#FFF',
              },
              {
                title: 'Accessories',
                badge: '40% OFF',
                image_url: '',
                background_color: '#FFF',
              },
            ],
          },
          version: 1,
        };
      default:
        return { data: {}, version: 1 };
    }
  }

  // ─── Toast ────────────────────────────────────────────────────────────────
  toast(type: ToastMessage['type'], message: string) {
    const id = ++this.toastCounter;
    this.toasts.push({ id, type, message });
    setTimeout(() => this.dismissToast(id), 4000);
  }

  dismissToast(id: number) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  getSafeSvg(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg || '');
  }
}
